import moment from 'moment';
import { Tooltip } from '@components';
import { UserAvatar } from './avatar';

interface UserInterface {
  id: number;
  first_name: string;
  last_name: string;
  avatar_url: string;
  initials: string;
  avatar_classes: string;
}

export const AUTH_STATUSES = {
  unauthorized: {
    color: { text: 'text-slate-500', bg: 'bg-slate-500' },
    title: 'Non connecté',
    text: "Le nom de l'intervenant apparaît dans le dossier mais il n'a pas été invité à y participer. Il est donc inactif sur Notiplus."
  },
  connected: {
    color: { text: 'text-green-500', bg: 'bg-green-500' },
    title: 'Connecté',
    text: "L'intervenant est bien connecté."
  },
  pending: {
    color: { text: 'text-amber-500', bg: 'bg-amber-500' },
    title: 'En attente',
    text: "L'intervenant reçoit les invitations et autres notifications mais il ne s'est pas encore connecté."
  },
  error: {
    color: { text: 'text-black', bg: 'bg-black' },
    title: 'En erreur',
    text: "Certaines notifications envoyées à l'intervenant ne parviennent pas à atteindre sa boîte de réception. Plusieurs raisons peuvent l'expliquer, comme une adresse email erronée, un filtre anti-spam très restrictif, etc..."
  }
};

export function AuthStatusComponent({ user }) {
  let authStatus = AUTH_STATUSES.unauthorized;

  if (user.last_login_at) {
    authStatus = AUTH_STATUSES.connected;
  } else if (user.bounced) {
    authStatus = AUTH_STATUSES.error;
  } else if (user.email) {
    authStatus = AUTH_STATUSES.pending;
  }

  return (
    <div className="leading-[13px]">
      <div className="flex space-x-1 items-center pb-[4px]">
        <div className={`w-[7px] h-[7px] rounded-full ${authStatus.color.bg}`}></div>
        <div className={`${authStatus.color.text} text-[10px]`}>{authStatus.title}</div>
      </div>
      <div className="font-hregular text-[10px]">{authStatus.text}</div>
    </div>
  );
}

export function resolveAuthStatus(authStatuses, user) {
  if (user.last_login_at || user.mobile_phone_confirmed_at) {
    return authStatuses['green'];
  }

  if (user.bounced) {
    return authStatuses['black'];
  }

  if (!user.email) {
    return authStatuses['slate'];
  }

  return authStatuses['amber'];
}

export function AvatarTooltipContent({ authTitle, authText, user, guestMode = false }) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center space-x-2">
        <div className="flex-shrink-0">
          <UserAvatar user={user} width="h-[30px] w-[30px]" />
        </div>
        <div className="flex-shrink truncate font-hmedium !text-[14px] flex flex-col">
          <span className="truncate">
            {user.first_name} {user.last_name} {user.name}
          </span>
          <span className="truncate text-xs text-slate-400">{user.email}</span>
        </div>
      </div>
      {!guestMode && (
        <div className="leading-[13px] mt-2">
          <div>
            <AuthStatusComponent user={user} />
          </div>
          {user.last_login_at && (
            <div className="font-hregular text-[10px]">
              Dernière connexion : {moment(user.last_login_at).format('D/MM/YY à H:m')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function RenderAvatar({
  width,
  pingSize = 'w-[7px] h-[7px]',
  authStatuses,
  user,
  userIndex = 0,
  guestMode
}) {
  const { title: authTitle, text: authText } = resolveAuthStatus(authStatuses, user) || {};

  let authStatus = AUTH_STATUSES.unauthorized;
  if (user.bounced) {
    authStatus = AUTH_STATUSES.error;
  } else if (user.last_login_at) {
    authStatus = AUTH_STATUSES.connected;
  } else if (user.email) {
    authStatus = AUTH_STATUSES.pending;
  }

  return (
    <div
      className={`[&>[data-state]]:h-[28px] [&>[data-state]]:w-[28px] [&>[data-state]]:block [&>[data-state]]:-mx-[4px] ${width}`}
    >
      <Tooltip
        color="white"
        content={
          <div className="w-[200px]">
            <AvatarTooltipContent
              guestMode={guestMode}
              authTitle={authTitle}
              authText={authText}
              user={user}
            />
          </div>
        }
      >
        <UserAvatar
          width={width}
          user={user}
          userIndex={userIndex}
          ping={
            <div
              className={`${pingSize} rounded-full ring-1 ring-white ${authStatus?.color?.bg}`}
            ></div>
          }
        />
      </Tooltip>
    </div>
  );
}

export function RenderAvatars({
  guestMode = false,
  authStatuses,
  users = []
}: {
  users: UserInterface[];
}) {
  const tooltipText = 'Modifier les intervenants';

  if (users.length <= 0) {
    return <div className="w-full text-center">-</div>;
  }

  const displayUsers = users.slice(0, 3);
  const remainingCount = users.length - 3;

  return (
    <div className={`flex items-center relative justify-center [&>[data-state]]:w-[25px]`}>
      {displayUsers.map((user, index) => (
        <RenderAvatar
          guestMode={guestMode}
          user={user}
          key={user.id}
          userIndex={index}
          authStatuses={authStatuses}
        />
      ))}
      {remainingCount > 0 && (
        <div className="font-hmedium text-[10px] rounded-full w-[28px] h-[28px] flex items-center justify-center bg-slate-100 z-[1] -ml-[2px] ml-1 text-sm text-gray-600">
          +{remainingCount}
        </div>
      )}
    </div>
  );
}
